import { cloneElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import { getSupportUserPerm } from '../../../../api/admin/rbac';
import { CustomPopover } from '../../../../components/custom-popover';
import getFormatMessage from '../../../../locales/get-format-message';
import { RbacAdminPermObjectKey } from '../../../../model/api-modules';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../../../store/modules/common/model';
import { IRootState } from '../../../../store/reducers';
import { NoPermissionCard } from '../../../component/permission-check/no-permission';

interface IProps {
  readonly contentType?: 'page' | 'action';
  readonly permissionKey?: RbacAdminPermObjectKey;
  children: JSX.Element;
  readonly hasPerm?: boolean;
  readonly otherTips?: string;
  readonly placement?:
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
    | 'leftTop'
    | 'leftBottom'
    | 'rightTop'
    | 'rightBottom';
}

let permsCache: RbacAdminPermObjectKey[] | undefined = undefined;

export const AdminPermCheck = (props: IProps) => {
  const {
    children,
    contentType = 'page',
    permissionKey,
    hasPerm,
    otherTips,
    placement,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const menuDispatch = useDispatch<Dispatch<CommonAction>>();
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const [userPerms, setUserPerms] =
    useState<readonly RbacAdminPermObjectKey[]>();

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (!checkPerm() && contentType === 'page') {
      menuDispatch({
        type: ReduxCommonActionType.SELECT_MENU_ID,
        result: null,
      });
    }
  }, [location]);

  const init = async () => {
    if (!userInfo) {
      return;
    }
    const res = permsCache || (await getSupportUserPerm(userInfo.UserID));
    if (res) {
      permsCache = [...res];
    }
    if (!res || !res.length) {
      history.push('/403');
      return;
    }
    setUserPerms(res);
  };

  const checkPerm = () => {
    if (hasPerm !== undefined) {
      return hasPerm;
    }
    if (!userPerms) {
      return false;
    }

    if (permissionKey) {
      return userPerms.includes(permissionKey);
    }
    const permPathMap: Record<
      string,
      RbacAdminPermObjectKey | RbacAdminPermObjectKey[]
    > = {
      user: RbacAdminPermObjectKey.UserManage,
      'admin-account': RbacAdminPermObjectKey.AdminAccount,
      'close-open': [
        RbacAdminPermObjectKey.GameManageView,
        RbacAdminPermObjectKey.GameManageEdit,
      ],
      'price-calculator': RbacAdminPermObjectKey.ServiceFeeManage,
      'company-auth': RbacAdminPermObjectKey.CompanyCertification,
      fee: RbacAdminPermObjectKey.FeeCenter,

      'create-service': RbacAdminPermObjectKey.ServiceManage,
      download: RbacAdminPermObjectKey.DownloadManage,
      'download-record': RbacAdminPermObjectKey.DownloadRecord,
      service: RbacAdminPermObjectKey.ServiceApply,
      'upgrade-notification': RbacAdminPermObjectKey.UpgradeNotification,
      'download-news': RbacAdminPermObjectKey.NewsManage,

      'mail-record': RbacAdminPermObjectKey.ContactUS,
      message: RbacAdminPermObjectKey.MessageManage,
      notice: RbacAdminPermObjectKey.BusinessNotice,
      business_account: RbacAdminPermObjectKey.ApplySupportAccount,
    };

    const pageName =
      location.pathname.match(/(?<=admin\/)[\w-]+(?=\/?)/)?.[0] || '';
    const permKeys = [permPathMap[pageName]].flat().filter(Boolean);

    if (
      permKeys.length &&
      permKeys.every((perm) => !userPerms.includes(perm))
    ) {
      return false;
    }
    return true;
  };
  if (!userPerms) {
    return null;
  }

  if (checkPerm()) {
    if (otherTips) {
      return (
        <CustomPopover content={otherTips} placement={placement || 'topLeft'}>
          <> {children}</>
        </CustomPopover>
      );
    }
    return children;
  }

  if (contentType === 'action') {
    return (
      <CustomPopover
        content={getFormatMessage('no_support_rbac_permission_action')}
        placement={placement || 'topLeft'}
      >
        <>
          {cloneElement(children, {
            disabled: true,
            onClickCapture: (ev: MouseEvent) => {
              ev.stopPropagation();
            },
          })}
        </>
      </CustomPopover>
    );
  }
  return (
    <NoPermissionCard
      title={getFormatMessage('no_page_permission_line_one')}
      subTitle={getFormatMessage('support_no_page_permission_line_two')}
    />
  );
};
